import {
  ChangeDetectionStrategy,
  Component,
  ComponentFactory,
  ComponentFactoryResolver,
  ComponentRef,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import {DomSanitizer, SafeStyle} from '@angular/platform-browser';
import {Logger} from '@nh/angular-tses';
import {DateService} from 'app/core/services/date.service';
import {SpecialistRecommendationComponent} from 'app/core/specialist-recommendation/specialist-recommendation.component';
import {TemplateStringComponent} from 'app/core/templatiser/template-string/template-string.component';
import {UserDisplayComponent} from 'app/core/user-display/user-display.component';
// eslint-disable-next-line max-len
import {DiaryWarningCardComponent} from 'app/departures/departure/diary-view/diary-event-card/diary-properties-card/diary-warning-card/diary-warning-card.component';
import {StyleableTextComponent} from './styleable-text/styleable-text.component';
import {TemplateRoleComponent} from './template-role/template-role.component';

export interface Template {
  readonly template: string;
  readonly arguments: (string | number | boolean)[];
}

@Component({
  selector: 'das-templatiser',
  templateUrl: './templatiser.component.html',
  styleUrls: ['./templatiser.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TemplatiserComponent implements OnChanges {
  @Input() public template: Template;

  @ViewChild('componentContainer', {read: ViewContainerRef, static: true}) public componentContainer: ViewContainerRef;

  private readonly _templateStringComponentFactory: ComponentFactory<TemplateStringComponent>;
  private readonly _userDisplayComponentFactory: ComponentFactory<UserDisplayComponent>;
  private readonly _warningDisplayComponentFactory: ComponentFactory<DiaryWarningCardComponent>;
  private readonly _specialistRecommendationComponentFactory: ComponentFactory<SpecialistRecommendationComponent>;
  private readonly _templateRoleComponentFactory: ComponentFactory<TemplateRoleComponent>;
  private readonly _styleableTextComponentFactory: ComponentFactory<StyleableTextComponent>;

  constructor(
    private _cfr: ComponentFactoryResolver,
    private _dateService: DateService,
    private _sanitizer: DomSanitizer
  ) {
    this._userDisplayComponentFactory = this._cfr.resolveComponentFactory<UserDisplayComponent>(UserDisplayComponent);
    this._warningDisplayComponentFactory = this._cfr.resolveComponentFactory<DiaryWarningCardComponent>(
      DiaryWarningCardComponent
    );
    this._specialistRecommendationComponentFactory = this._cfr.resolveComponentFactory<
      SpecialistRecommendationComponent
    >(SpecialistRecommendationComponent);
    this._templateStringComponentFactory = this._cfr.resolveComponentFactory<TemplateStringComponent>(
      TemplateStringComponent
    );
    this._templateRoleComponentFactory = this._cfr.resolveComponentFactory<TemplateRoleComponent>(
      TemplateRoleComponent
    );
    this._styleableTextComponentFactory = this._cfr.resolveComponentFactory<StyleableTextComponent>(
      StyleableTextComponent
    );
  }

  public ngOnChanges(changes: SimpleChanges): void {
    this._templatise();
  }

  private _templatise(): void {
    this.componentContainer.clear();

    if (!this.template || !this.template.template || !this.template.arguments) {
      return;
    }

    const tokens: string[] = this.template ? this.template.template.split(/(\$\{\S+\})/g) : [];
    let interpolationTokensVisited: number = 0;

    tokens.forEach((token: string) => {
      if (this.isUserInterpolationToken(token)) {
        const component: ComponentRef<UserDisplayComponent> = this.componentContainer.createComponent(
          this._userDisplayComponentFactory
        );
        component.instance.userId = this.getArgument(interpolationTokensVisited++) as string;
      } else if (this.isRecommendationInterpolationToken(token)) {
        const component: ComponentRef<SpecialistRecommendationComponent> = this.componentContainer.createComponent(
          this._specialistRecommendationComponentFactory
        );
        component.instance.recommendation = this.getArgument(interpolationTokensVisited++) as any;
      } else if (this.isDateInterpolationToken(token)) {
        this.addDateToken(this.getArgument(interpolationTokensVisited++));
      } else if (this.isDurationInterpolationToken(token)) {
        this.addDurationToken(
          this.getArgument(interpolationTokensVisited++),
          this.getArgument(interpolationTokensVisited++)
        );
      } else if (this.isRoleInterpolationToken(token)) {
        const component: ComponentRef<TemplateRoleComponent> = this.componentContainer.createComponent(
          this._templateRoleComponentFactory
        );
        component.instance.role = this.getArgument(interpolationTokensVisited++) as any;
      } else if (this.isWarningInterpolationToken(token)) {
        this.componentContainer.createComponent(this._warningDisplayComponentFactory);
      } else if (this.isInterpolationToken(token)) {
        this.addTextToken(this.getArgument(interpolationTokensVisited++));
      } else {
        this.addTextToken(token);
      }
    });
  }

  private isInterpolationToken(t: string): boolean {
    return t && t.length > 3 && t.startsWith('${') && t.endsWith('}');
  }

  private isUserInterpolationToken(t: string): boolean {
    return t === '${user}';
  }

  private isRecommendationInterpolationToken(t: string): boolean {
    return t === '${rec}';
  }

  private isDateInterpolationToken(t: string): boolean {
    return t === '${date}';
  }

  private isDurationInterpolationToken(t: string): boolean {
    return t === '${duration}';
  }

  private isRoleInterpolationToken(t: string): boolean {
    return t === '${role}';
  }

  private isWarningInterpolationToken(t: string): boolean {
    return t === '${warning}';
  }

  private getArgument(argumentArrayIndex: number): string | number | boolean {
    if (this.template.arguments && this.template.arguments.length > argumentArrayIndex) {
      return this.template.arguments[argumentArrayIndex];
    } else {
      Logger.error('templatiser-error', `Wrong number of arguments to templatiser for "${this.template}"`);
      return null;
    }
  }

  private addDateToken(t: string | number | boolean): void {
    this.addTextToken(this._dateService.formatDate(Number(t)));
  }

  private addDurationToken(t: string | number | boolean, t2: string | number | boolean): void {
    if (Boolean(t2)) {
      this.addTextToken(this._dateService.getDuration(Number(t)));
    } else {
      const style: SafeStyle =
        this._dateService.getTimeInDays(Number(t)) > 0
          ? this._sanitizer.bypassSecurityTrustStyle(`color: black;`)
          : this._sanitizer.bypassSecurityTrustStyle(`font-weight: bold; color: red;`);
      this.addStyleableTextComponent(this._dateService.getTimeLeftInSesAsString(Number(t)), style);
    }
  }

  private addStyleableTextComponent(text: string, style: SafeStyle) {
    const component: ComponentRef<StyleableTextComponent> = this.componentContainer.createComponent(
      this._styleableTextComponentFactory
    );
    component.instance.style = style;
    component.instance.text = text;
  }

  private addTextToken(t: string | number | boolean): void {
    const token: string = '' + t;
    if (token) {
      const component: ComponentRef<TemplateStringComponent> = this.componentContainer.createComponent(
        this._templateStringComponentFactory
      );
      component.instance.token = token;
    }
  }
}
